import {
  DownloadOutlined,
  KeyOutlined,
  QrcodeOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { message, Modal } from 'antd';
import { MODAL_NAME } from 'constants/modal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import API from 'api';
import TextField from 'views/common/TextField';
import classNames from 'classnames';
import { disable2Fa, enable2Fa } from 'store/reducers/auth';
import { closeModal } from 'store/reducers/modal';

const Block2Fa = (props) => {
  const { left, right, isShow, warning } = props;
  return (
    isShow && (
      <div
        className={classNames('twofa-authentication-block', {
          'twofa-authentication-block--warning': warning,
        })}
      >
        <div className="twofa-authentication-block__left">
          <div className="twofa-authentication-block__icon">{left}</div>
        </div>
        <div className="twofa-authentication-block__right">{right}</div>
      </div>
    )
  );
};

const PopupEnable2FaAuthentication = () => {
  const { use2Fa } = useSelector((state) => state.auth);
  const { open } = useSelector(state => state.modal[MODAL_NAME.ENABLE_2FA]) || {};
  const [qrcode, setQrcode] = useState();
  const [code, setCode] = useState({});
  const [secretCode, setSecretCode] = useState();
  const dispatch = useDispatch();

  const onCloseModal = () => {
    dispatch(closeModal({ name: MODAL_NAME.ENABLE_2FA }));
  };

  const get2FaCode = async () => {
    const res = await API.getSecret2FaCode();
    if (res.status === 200) {
      setSecretCode(res.data);
    }
  };

  const getQrCode = async () => {
    const res = await API.getQrCodeAuthen2Fa();
    if (res.status === 200) {
      get2FaCode();
      setQrcode(URL.createObjectURL(res.data));
    } else {
      message.error('There is an error in the system, please try again later');
    }
  };

  const onChangeCode = (e) => {
    setCode({ value: e.target.value });
  };

  const validateCode = () => {
    if (!code.value) {
      return 'Code is required.';
    }

    if (code?.value?.length !== 6 || /[^0-9]/.test(code.value)) {
      return 'Code must have 6 number.';
    }
  };

  const onOK = async () => {
    const error = validateCode();
    if (error) {
      return setCode({ ...code, error });
    }
    let res;
    if (use2Fa) {
      res = await API.disable2FaAuthentication({ otp: code.value });
    } else {
      res = await API.enable2FaAuthentication({ otp: code.value });
    }

    if (res.status === 200) {
      message.success(use2Fa ? 'Disabled 2fa success' : 'Enable 2fa success');
      if (use2Fa) {
        dispatch(disable2Fa());
      } else {
        dispatch(enable2Fa());
      }

      onCloseModal();
    } else {
      setCode({ ...code, error: res.message });
    }
  };

  useEffect(() => {
    setQrcode();
    setSecretCode();
    setCode({});
    if (!use2Fa && open) {
      getQrCode();
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <Modal
      open={open}
      className="twofa-authentication-modal"
      onCancel={onCloseModal}
      title={
        use2Fa ? 'Disable 2FA Authentication' : 'Enable 2FA Authentication'
      }
      okText={use2Fa ? 'Disable 2FA' : 'Enable 2FA'}
      onOk={onOK}
      okButtonProps={{ className: use2Fa && 'btn-warning' }}
    >
      <Block2Fa
        isShow={!use2Fa}
        left={<DownloadOutlined />}
        right={
          <div className="twofa-step-block">
            <div className="twofa-step-block__label">Step 1</div>
            <div className="twofa-step-block__description">
              Install a two-factor authentication program (Google Authenticator)
            </div>
          </div>
        }
      />
      <Block2Fa
        isShow={!use2Fa}
        left={<QrcodeOutlined />}
        right={
          <div className="twofa-step-block">
            <div className="twofa-step-block__label">Step 2</div>
            <div className="twofa-step-block__description">
              Open up your Google Authenticator 2FA mobile app in your phone and
              scan the following QR-barcode
            </div>
            {qrcode && (
              <div className="twofa-step-block__qrcode">
                <img src={qrcode} alt="qrcode 2fa" />
              </div>
            )}
            {secretCode && (
              <div className="twofa-step-block__secret">
                <div className="desc">
                  If your mobile app does not read QR-code, enter these code:
                </div>
                <div className="code">{secretCode}</div>
              </div>
            )}
          </div>
        }
      />
      <Block2Fa
        isShow={!use2Fa}
        left={<KeyOutlined />}
        right={
          <div className="twofa-step-block">
            <div className="twofa-step-block__label">Step 3</div>
            <div className="twofa-step-block__description">
              Enter the 2FA code displayed in the app to confirm and enable 2FA
              for your account
            </div>
            <div className="twofa-step-block__code">
              <TextField
                onChange={onChangeCode}
                value={code.value}
                placeholder="Enter 2FA code"
                helperText={code.error}
              />
            </div>
          </div>
        }
      />

      <Block2Fa
        isShow={use2Fa}
        left={<WarningOutlined />}
        warning
        right={
          <div className="twofa-step-block">
            <div className="twofa-step-block__label twofa-step-block__label--warning">
              Warning
            </div>
            <div className="twofa-step-block__description">
              By removing two-factor authentication, your account is at risk of
              hacker attacks
            </div>
          </div>
        }
      />
      <Block2Fa
        isShow={use2Fa}
        left={<KeyOutlined />}
        right={
          <div className="twofa-step-block">
            <div className="twofa-step-block__label">
              Disable 2FA authentication
            </div>
            <div className="twofa-step-block__description">
              Enter the 2FA code displayed in the app to confirm and disable 2FA
              from your account
            </div>
            <div className="twofa-step-block__code">
              <TextField
                onChange={onChangeCode}
                value={code.value}
                placeholder="Enter 2FA code"
                helperText={code.error}
              />
            </div>
          </div>
        }
      />
    </Modal>
  );
};

export default PopupEnable2FaAuthentication;
